import React from 'react';
import { Button, Typography } from '@mui/material';
import { ErrorOutline, RefreshOutlined } from '@mui/icons-material';
import styled from '@emotion/styled';
import { useBrandName } from '../utils/brandUtils';

const PageContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(6, 3),
    minHeight: '50vh',
}));

const WidgetContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(3, 2),
    height: '100%',
    color: theme.palette.text.secondary,
}));

const StyledIcon = styled(ErrorOutline)(({ theme }) => ({
    fontSize: '3rem',
    color: theme.palette.text.disabled,
}));

const StyledWidgetIcon = styled(ErrorOutline)(({ theme }) => ({
    fontSize: '2rem',
    color: theme.palette.text.disabled,
}));

const ActionRow = styled('div')(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
}));

const PageFallback = ({ onRetry, onReload }) => {
    const brandName = useBrandName();

    return (
        <PageContainer>
            <StyledIcon />
            <Typography variant="h6" component="h2">
                Something went wrong on this page
            </Typography>
            <Typography variant="body2" color="textSecondary">
                The rest of {brandName} is still working. Try again, or reload the page if it keeps happening.
            </Typography>
            <ActionRow>
                <Button variant="contained" color="primary" startIcon={<RefreshOutlined />} onClick={onRetry}>
                    Try again
                </Button>
                <Button variant="outlined" onClick={onReload}>
                    Reload page
                </Button>
            </ActionRow>
        </PageContainer>
    );
};

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
        this.handleRetry = this.handleRetry.bind(this);
        this.handleReload = this.handleReload.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, errorInfo) {
        console.error(`[ErrorBoundary${this.props.label ? `: ${this.props.label}` : ''}]`, error, errorInfo?.componentStack);
    }

    // Navigating away must clear the error, or one crashed page keeps rendering the fallback forever.
    componentDidUpdate(prevProps) {
        if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
            this.setState({ error: null });
        }
    }

    handleRetry() {
        this.setState({ error: null });
    }

    handleReload() {
        window.location.reload();
    }

    render() {
        if (!this.state.error) {
            return this.props.children;
        }

        if (this.props.variant === 'widget') {
            return (
                <WidgetContainer>
                    <StyledWidgetIcon />
                    <Typography variant="body2">
                        {this.props.label ? `${this.props.label} couldn't be displayed.` : "This widget couldn't be displayed."}
                    </Typography>
                    <Button size="small" startIcon={<RefreshOutlined />} onClick={this.handleRetry}>
                        Try again
                    </Button>
                </WidgetContainer>
            );
        }

        return <PageFallback onRetry={this.handleRetry} onReload={this.handleReload} />;
    }
}

export default ErrorBoundary;
